import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, X, Lightbulb, Rocket, TrendingUp, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SERVICES } from "@/data/services";

const STAGES = [
  { id: "idee", icon: Lightbulb, label: "Encore une idée", desc: "Je n'ai pas encore lancé l'activité" },
  { id: "lancement", icon: Rocket, label: "Je viens de lancer", desc: "Moins de 12 mois d'activité" },
  { id: "croissance", icon: TrendingUp, label: "Activité en croissance", desc: "Je veux scaler maintenant" },
  { id: "etabli", icon: Building2, label: "Entreprise établie", desc: "Je veux dupliquer / franchiser" },
];

// Priorités = nos 7 services officiels (sans réinvention)
const PRIORITIES = SERVICES.map((s) => ({
  id: s.id,
  icon: s.icon,
  label: s.short,
  desc: s.desc.length > 80 ? s.desc.slice(0, 78) + "…" : s.desc,
}));

const URGENCY = [
  { id: "urgent", label: "Urgent (< 1 mois)", color: "#FFDCA8" },
  { id: "3m", label: "Sous 3 mois", color: "#06B6D4" },
  { id: "6m", label: "Sous 6 mois", color: "#A7F3D0" },
  { id: "flexible", label: "Flexible", color: "#94a3b8" },
];

const KEY = "besson_qual_done";

export default function OnboardingModal({ open, onClose, onComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = intro, 1 = stage, 2 = priority, 3 = urgency, 4 = done
  const [stage, setStage] = useState("");
  const [priority, setPriority] = useState("");
  const [urgency, setUrgency] = useState("");

  useEffect(() => {
    if (!open) return;
    // disable body scroll
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const validate = () => {
    const payload = { stage, priority, urgency };
    try { localStorage.setItem(KEY, JSON.stringify(payload)); } catch {}
    onComplete && onComplete(payload);
    onClose && onClose();
    // Redirect to the dedicated stage page
    if (stage) {
      setTimeout(() => navigate(`/etape/${stage}`), 50);
    }
  };

  const skip = () => {
    try { localStorage.setItem(KEY, JSON.stringify({ stage: "", priority: "", urgency: "", skipped: true })); } catch {}
    onClose && onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#0A1128]/85 backdrop-blur-md" data-testid="onboarding-modal">
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Skip button (top-right, discreet) */}
        <button onClick={skip} data-testid="onboarding-skip" className="absolute top-4 right-4 text-xs text-[#94a3b8] hover:text-[#0A1128] flex items-center gap-1 z-10">
          Passer<X size={12} />
        </button>

        {/* Progress */}
        {step > 0 && step < 4 && (
          <div className="absolute top-0 inset-x-0 h-1 bg-[#FAFAFA]">
            <div className="h-full bg-gradient-to-r from-[#06B6D4] to-[#FFDCA8] transition-all" style={{ width: `${(step / 3) * 100}%` }} />
          </div>
        )}

        {/* STEP 0 — Welcome */}
        {step === 0 && (
          <div className="p-8 sm:p-10 text-center" data-testid="modal-welcome">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0A1128] to-[#14213d] mb-5 shadow-lg">
              <Sparkles className="text-[#67E8F9]" size={28} />
            </div>
            <div className="text-[11px] font-extrabold tracking-[0.3em] uppercase text-[#06B6D4]">Bienvenue</div>
            <h2 className="display mt-2 text-3xl sm:text-4xl font-extrabold text-[#0A1128] leading-tight">Avant de commencer,<br />faisons connaissance.</h2>
            <p className="mt-4 text-[#475569] max-w-md mx-auto">3 questions rapides pour vous orienter vers la <b>bonne solution</b> et la <b>bonne étape</b> de votre parcours entrepreneur.</p>
            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md mx-auto text-xs text-[#0A1128]">
              <Stat n="1" label="Votre stade" color="#FFDCA8" />
              <Stat n="2" label="Votre priorité" color="#06B6D4" />
              <Stat n="3" label="Votre urgence" color="#A7F3D0" />
            </div>
            <Button onClick={() => setStep(1)} data-testid="modal-start" className="mt-7 bg-[#0A1128] hover:bg-[#14213d] text-white px-8 py-6 text-base font-bold rounded-full">
              C'est parti — 60 secondes <ArrowRight size={16} className="ml-2" />
            </Button>
            <div className="mt-3 text-[11px] text-[#94a3b8]">Sans inscription · Sans engagement</div>
          </div>
        )}

        {/* STEP 1 — Stage */}
        {step === 1 && (
          <ModalStep title="Où en êtes-vous ?" subtitle="Étape 1 / 3" onBack={() => setStep(0)} onNext={() => setStep(2)} disableNext={!stage}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {STAGES.map((s) => (
                <button key={s.id} data-testid={`modal-stage-${s.id}`} onClick={() => { setStage(s.id); setTimeout(() => setStep(2), 200); }} className={`group text-left p-5 rounded-2xl border-2 transition-all hover:scale-[1.02] ${stage === s.id ? "border-[#06B6D4] bg-[#06B6D4]/5 shadow-lg" : "border-black/10 hover:border-[#06B6D4]/50"}`}>
                  <s.icon size={22} className={stage === s.id ? "text-[#06B6D4]" : "text-[#0A1128]"} />
                  <div className="display font-bold text-[#0A1128] mt-2">{s.label}</div>
                  <div className="text-xs text-[#64748B] mt-0.5">{s.desc}</div>
                </button>
              ))}
            </div>
          </ModalStep>
        )}

        {/* STEP 2 — Priority */}
        {step === 2 && (
          <ModalStep title="Quelle est votre priorité ?" subtitle="Étape 2 / 3 · Choisissez ce qui vous parle le plus" onBack={() => setStep(1)} onNext={() => setStep(3)} disableNext={!priority}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PRIORITIES.map((p) => (
                <button key={p.id} data-testid={`modal-priority-${p.id}`} onClick={() => { setPriority(p.id); setTimeout(() => setStep(3), 200); }} className={`group flex items-center gap-3 text-left p-4 rounded-2xl border-2 transition-all hover:scale-[1.02] ${priority === p.id ? "border-[#06B6D4] bg-[#06B6D4]/5 shadow-lg" : "border-black/10 hover:border-[#06B6D4]/50"}`}>
                  <span className="w-11 h-11 rounded-xl bg-[#FAFAFA] flex items-center justify-center flex-shrink-0"><p.icon size={18} className="text-[#0A1128]" /></span>
                  <div>
                    <div className="display font-bold text-[#0A1128] text-sm">{p.label}</div>
                    <div className="text-xs text-[#64748B]">{p.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </ModalStep>
        )}

        {/* STEP 3 — Urgency */}
        {step === 3 && (
          <ModalStep title="Quelle est votre urgence ?" subtitle="Étape 3 / 3" onBack={() => setStep(2)} onNext={validate} disableNext={!urgency} nextLabel="Voir ma feuille de route →">
            <div className="grid grid-cols-2 gap-3">
              {URGENCY.map((u) => (
                <button key={u.id} data-testid={`modal-urgency-${u.id}`} onClick={() => setUrgency(u.id)} className={`text-left p-4 rounded-2xl border-2 transition-all hover:scale-[1.02] ${urgency === u.id ? "border-[#06B6D4] bg-[#06B6D4]/5 shadow-lg" : "border-black/10 hover:border-[#06B6D4]/50"}`}>
                  <div className="w-3 h-3 rounded-full" style={{ background: u.color }} />
                  <div className="display font-bold text-[#0A1128] mt-2">{u.label}</div>
                </button>
              ))}
            </div>
          </ModalStep>
        )}
      </div>
    </div>
  );
}

function ModalStep({ title, subtitle, onBack, onNext, children, disableNext, nextLabel = "Suivant" }) {
  return (
    <div className="p-7 sm:p-9">
      {subtitle && <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-[#06B6D4]">{subtitle}</div>}
      <h3 className="display mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A1128] leading-tight">{title}</h3>
      <div className="mt-6">{children}</div>
      <div className="mt-6 flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-[#64748B] hover:text-[#0A1128]">← Retour</button>
        <Button onClick={onNext} disabled={disableNext} data-testid="modal-next" className="bg-[#0A1128] hover:bg-[#14213d] text-white rounded-full">{nextLabel} <ArrowRight size={14} className="ml-1" /></Button>
      </div>
    </div>
  );
}

function Stat({ n, label, color }) {
  return (
    <div className="flex flex-col items-center bg-[#FAFAFA] rounded-xl p-3 border border-black/8">
      <div className="display text-xl font-extrabold" style={{ color }}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#64748B] mt-0.5">{label}</div>
    </div>
  );
}

// Export helper to read the qual answers anywhere
export function getQualAnswers() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}
export function hasQualified() {
  return Boolean(localStorage.getItem(KEY));
}
